import React, { useEffect, useState } from 'react';
import styles from './CategoryTree.module.css';
import cx from 'classnames';
import { faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Category } from '../../../../types/Category';

interface CategoryTreeProps {
    categories: Category[];
    loading?: boolean;
    onCategorySelect?: (categoryId: number) => void;
    selectedCategoryId?: number | null;
}

const CategoryTree: React.FC<CategoryTreeProps> = ({
    categories,
    loading = false,
    onCategorySelect,
    selectedCategoryId = null,
}) => {
    const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (categories.length > 0) {
            setExpandedCategories(new Set(categories.map((category) => category.id)));
        } else {
            setExpandedCategories(new Set());
        }
    }, [categories]);

    const toggleExpand = (categoryId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            return newSet;
        });
    };

    const handleCategoryClick = (categoryId: number) => {
        if (onCategorySelect) {
            onCategorySelect(categoryId);
        }
    };

    if (loading) {
        return (
            <div className={cx(styles['category-tree-container'])}>
                <div className={cx(styles['category-tree-loading'])}>
                    Kategorien werden geladen...
                </div>
            </div>
        );
    }

    if (categories.length === 0) {
        return (
            <div className={cx(styles['category-tree-container'])}>
                <div className={cx(styles['category-tree-empty'])}>
                    Keine Kategorien verfügbar.
                </div>
            </div>
        );
    }

    const renderCategory = (category: Category, level: number = 0): React.ReactNode => {
        const hasChildren = category.children && category.children.length > 0;
        const isExpanded = expandedCategories.has(category.id);
        const isSelected = selectedCategoryId === category.id;

        return (
            <li key={category.id} className={cx(styles['category-tree-item'])}>
                <div
                    className={cx(styles['category-tree-row'], {
                        [styles['category-tree-row-active']]: isSelected
                    })}
                    style={{ paddingLeft: `${0.5 + level * 1.25}rem` }}
                >
                    {hasChildren && (
                        <button
                            type="button"
                            className={cx(styles['category-tree-expand'])}
                            onClick={(e) => toggleExpand(category.id, e)}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                            <FontAwesomeIcon
                                icon={isExpanded ? faChevronDown : faChevronRight}
                                className={cx(styles['category-tree-icon'])}
                            />
                        </button>
                    )}
                    {!hasChildren && (
                        <span className={cx(styles['category-tree-spacer'])} />
                    )}
                    <button
                        type="button"
                        className={cx(styles['category-tree-button'], {
                            [styles['category-tree-button-active']]: isSelected
                        })}
                        onClick={() => handleCategoryClick(category.id)}
                    >
                        {category.name}
                    </button>
                </div>
                {hasChildren && (
                    <div
                        className={cx(styles['category-tree-children'], {
                            [styles['category-tree-children-expanded']]: isExpanded
                        })}
                    >
                        <ul className={cx(styles['category-tree-list'])}>
                            {category.children!.map((child) => renderCategory(child, level + 1))}
                        </ul>
                    </div>
                )}
            </li>
        );
    };

    return (
        <div className={cx(styles['category-tree-container'])}>
            <div className={cx(styles['category-tree-header'])}>Kategorien</div>
            <div className={cx(styles['category-tree-wrapper'])}>
                <ul className={cx(styles['category-tree-list'])}>
                    {categories.map((category) => renderCategory(category))}
                </ul>
            </div>
        </div>
    );
};

export default CategoryTree;
